import React, { useState, useEffect, useCallback } from 'react';
import '../IpPatientDetails.css';
import {
  Box, Button, ButtonGroup, Checkbox, FormControl, IconButton, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, TablePagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import Autocomplete from '@mui/material/Autocomplete';
import { CommonPatientDetails } from '../../../../../Components/Common Components/CommonComponents';
import { getCachevalue, localGetItem } from '../../../../../Utility/Services/CacheProviderService';
import RestAPIService from '../../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../../Utility/API/Serviceurls';
import { instructionList } from '../../../../../Utility/Constants';
import { debounce } from '../../../../../Components/CommonFunctions/CommonFunctions';
import CommonValidation from '../../../../../Components/CommonFunctions/CommonValidation';
import ToastMsg from '../../../../../Components/ToastMsg/ToastMsg';
import Loader from '../../../../../Components/Loader';
import { Colors } from '../../../../../Styles/Colors';

const STATUS_TABS = [
  { key: 'request', label: 'Request', statusFilter: 'Pending' },
  { key: 'billed', label: 'Billed', statusFilter: 'Billed' },
  // { key: 'cancelled', label: 'Cancelled', statusFilter: 'Cancelled' },
];

const DURATION_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const DURATION_MODE_MAP = { 1: 'Day(s)', 2: 'Week(s)', 3: 'Month(s)' };

const emptyDrugRow = () => ({ id: null, drug_ms_id: null, drug_name: '', quantity: '', uom: 'g', medicineList: [] });

export default function IPMedication() {
  const [patientData, setPatientData] = useState({});
  const [withOutAppointment, setWithOutAppointment] = useState({});
  const [list, setList] = useState([]);
  const [statusTab, setStatusTab] = useState('request');
  const [selectedPrescriptionIds, setSelectedPrescriptionIds] = useState([]);
  const [pharmacyList, setPharmacyList] = useState([]);
  const [pharmacyId, setPharmacyId] = useState(null);
  const [drugRows, setDrugRows] = useState([emptyDrugRow()]);
  const [morning, setMorning] = useState(true);
  const [afternoon, setAfternoon] = useState(false);
  const [evening, setEvening] = useState(true);
  const [night, setNight] = useState(false);
  const [duration, setDuration] = useState('');
  const [durationMode, setDurationMode] = useState(1);
  const [instructions, setInstructions] = useState('');
  const [comments, setComments] = useState('');
  const [editingPrescriptionId, setEditingPrescriptionId] = useState(null);
  const [editingDetailId, setEditingDetailId] = useState(null);
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const UserData = JSON.parse(localGetItem('loggedInUserInfo'));
  const ip_admission_id = patientData?.id || withOutAppointment?.ip_admission_id;

  const fetchPatient = useCallback(() => {
    const raw = getCachevalue('IppatientData');
    if (!raw) return;
    try {
      setPatientData(JSON.parse(raw));
    } catch (e) { }
    const appRaw = getCachevalue('withoutAppointment');
    if (appRaw) {
      try {
        const app = JSON.parse(appRaw);
        if (app?.patient_id === JSON.parse(raw)?.patient_id) setWithOutAppointment(app);
      } catch (e) { }
    }
  }, []);

  const fetchList = useCallback(() => {
    if (!ip_admission_id) return;
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_MEDICATION_DETAILS}?ip_admission_id=${ip_admission_id}`)
      .then((res) => {
        if (res.data?.status === 'success') setList(Array.isArray(res.data?.data) ? res.data.data : []);
        else setList([]);
        setIsLoader(false);
      })
      .catch(() => { setList([]); setIsLoader(false); });
  }, [ip_admission_id]);

  const fetchPharmacyList = useCallback(() => {
    RestAPIService.getAll(Serviceurls.AYURVEDA_PHARMACY_LIST)
      .then((res) => {
        if (res.data?.status === 'success' && Array.isArray(res.data?.data)) {
          setPharmacyList(res.data.data);
          if (res.data.data.length > 0) {
            setPharmacyId((prev) => prev ?? res.data.data[0]?.pharmacy_id ?? res.data.data[0]?.id);
          }
        } else setPharmacyList([]);
      })
      .catch(() => setPharmacyList([]));
  }, []);

  useEffect(() => { fetchPatient(); }, [fetchPatient]);
  useEffect(() => { if (ip_admission_id) fetchList(); }, [ip_admission_id, fetchList]);
  useEffect(() => { fetchPharmacyList(); }, [fetchPharmacyList]);

  const showError = (msg) => { setIsErrorMsg(true); setIsErrorMsgText(msg); };
  const showSuccess = (msg) => { setSuccessMsg(true); setSuccessMsgText(msg); };
  const msgClose = () => { setIsErrorMsg(false); setSuccessMsg(false); };

  const statusFilter = STATUS_TABS.find((t) => t.key === statusTab)?.statusFilter || 'Pending';
  const filteredList = list.filter((row) => {
    const s = row.prescription_status || row.status || '';
    if (statusTab === 'request') return s === 'Pending' || s === 'Transmitted';
    return s === statusFilter;
  });

  const togglePrescriptionSelection = (id, checked) => {
    setSelectedPrescriptionIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const handleTransferDrugs = () => {
    if (selectedPrescriptionIds.length === 0) {
      showError('Select at least one prescription to transmit.');
      return;
    }
    if (statusTab !== 'request') {
      showError('Only Request (Pending) prescriptions can be transmitted.');
      return;
    }
    setIsLoader(true);
    RestAPIService.updateWithOutId({ prescription_id: selectedPrescriptionIds }, Serviceurls.IP_MEDICATION_DETAILS)
      .then((res) => {
        setIsLoader(false);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Transmitted successfully.');
          setSelectedPrescriptionIds([]);
          fetchList();
        } else showError(res.data?.message || 'Transmit failed.');
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Transmit failed.');
      });
  };

  const handleDelete = (prescription_id) => {
    if (!prescription_id) return;
    setIsLoader(true);
    RestAPIService.delete(`${Serviceurls.IP_MEDICATION_DETAILS}?prescription_id=${prescription_id}`, {})
      .then((res) => {
        setIsLoader(false);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Deleted.');
          fetchList();
        } else showError(res.data?.message || 'Delete failed.');
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Delete failed.');
      });
  };

  const addDrugRow = () => setDrugRows((prev) => [...prev, emptyDrugRow()]);
  const removeDrugRow = (index) => setDrugRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const setDrugRow = (index, field, value) => {
    if (field === 'quantity' && value.length > 5) {
      return;
    }
    setDrugRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }

    const setDrugRowMedicineList = (index, medicineList) => {
      setDrugRows((prev) => prev.map((row, i) => (i === index ? { ...row, medicineList } : row)));
    };

    const drugSearchApi = useCallback(
      debounce((searchValue, rowIndex) => {
        if (!pharmacyId || !searchValue || String(searchValue).trim().length < 2) {
          if (searchValue?.length < 2) setDrugRowMedicineList(rowIndex, []);
          return;
        }
        const params = `pharmacy_id=${pharmacyId}&brand_name=${encodeURIComponent(searchValue)}&generic_name=`;
        RestAPIService.getAll(`${Serviceurls.AYURVEDA_DRUG_SEARCH}?${params}`)
          .then((res) => {
            const results = res.data?.data || [];
            setDrugRowMedicineList(rowIndex, Array.isArray(results) ? results : []);
          })
          .catch(() => setDrugRowMedicineList(rowIndex, []));
      }, 300),
      [pharmacyId]
    );

    const handleDrugSelect = (index, option) => {
      if (!option?.drug_ms_id || !pharmacyId) return;
      RestAPIService.getAll(`${Serviceurls.AYURVEDA_DRUG_DETAILS}?pharmacy_id=${pharmacyId}&drug_ms_id=${option.drug_ms_id}`)
        .then((res) => {
          if (res.data?.status !== 'success') return;
          const data = res.data?.data?.[0] || {};
          const uom = data.uom || option.uom || 'g';
          setDrugRows((prev) =>
            prev.map((row, i) =>
              i === index
                ? {
                  ...row,
                  drug_ms_id: data.drug_ms_id ?? option.drug_ms_id,
                  drug_name: data.drug_name ?? option.drug_name ?? '',
                  uom,
                  medicineList: row.medicineList || [],
                }
                : row
            )
          );
        })
        .catch(() => { });
    };

    const setDrugRowFromOption = (index, option) => {
      if (!option) {
        setDrugRows((prev) =>
          prev.map((row, i) =>
            i === index
              ? { ...row, id: null, drug_ms_id: null, drug_name: '', quantity: '', uom: 'g', medicineList: [] }
              : row
          )
        );
        return;
      }
      handleDrugSelect(index, option);
    };

    const handleClearForm = () => {
      setDrugRows([emptyDrugRow()]);
      setMorning(true);
      setAfternoon(false);
      setEvening(true);
      setNight(false);
      setDuration('');
      setDurationMode(1);
      setInstructions('');
      setComments('');
      setEditingPrescriptionId(null);
      setEditingDetailId(null);
    };

    const handleAddOrUpdate = () => {
      if (!ip_admission_id) { showError('Patient context missing.'); return; }
      if (!pharmacyId) { showError('Please select Pharmacy.'); return; }

      const hasAnyDrugInput = drugRows.some((r) => (r.drug_ms_id != null || (r.drug_name && r.drug_name.trim()) || (r.quantity !== '' && r.quantity != null)));
      if (!hasAnyDrugInput) { showError('Add at least one drug.'); return; }

      const rowsWithDrugNoQty = drugRows.filter((r) => (r.drug_ms_id != null || (r.drug_name && r.drug_name.trim())) && (r.quantity === '' || r.quantity == null || Number(r.quantity) <= 0));
      if (rowsWithDrugNoQty.length > 0) { showError('Enter quantity for all selected drugs.'); return; }

      const rowsWithQtyNoDrug = drugRows.filter((r) => (r.quantity !== '' && r.quantity != null && Number(r.quantity) > 0) && (r.drug_ms_id == null && !(r.drug_name && r.drug_name.trim())));
      if (rowsWithQtyNoDrug.length > 0) { showError('Select drug name for all rows where quantity is entered.'); return; }

      const validDrugs = drugRows.filter((r) => r.drug_ms_id != null && r.quantity !== '' && r.quantity != null && Number(r.quantity) > 0);
      if (validDrugs.length === 0) { showError('Add at least one drug with quantity greater than zero.'); return; }

      if (!duration || duration === '' || Number(duration) < 1) { showError('Please select Duration.'); return; }

      const instructionValue = instructions && typeof instructions === 'object' ? instructions?.value : instructions;
      if (!instructionValue || String(instructionValue).trim() === '') { showError('Please select Usage.'); return; }

      const payload = {
        ip_admission_id,
        pharmacy_id: pharmacyId,
        prescription_id: editingPrescriptionId || null,
        id: editingDetailId || null,
        morning,
        afternoon,
        evening,
        night,
        instructions: instructionValue || '',
        duration: String(duration),
        duration_mode: durationMode,
        comments: comments || '',
        deleted_ids: [],
        drugs: validDrugs.map((d) => ({
          id: d.id || null,
          drug_ms_id: d.drug_ms_id,
          quantity: Number(d.quantity),
          uom: d.uom || 'g',
        })),
      };
      setIsLoader(true);
      RestAPIService.create(payload, Serviceurls.IP_MEDICATION_DETAILS)
        .then((res) => {
          setIsLoader(false);
          if (res.data?.status === 'success') {
            showSuccess(res.data?.message || 'Saved.');
            handleClearForm();
            fetchList();
          } else showError(res.data?.message || 'Save failed.');
        })
        .catch((err) => {
          setIsLoader(false);
          showError(err?.response?.data?.message || err?.message || 'Save failed.');
        });
    };

    const handleEdit = (row) => {
      setEditingPrescriptionId(row.prescription_id);
      setEditingDetailId(row.id);
      setPharmacyId(row.pharmacy_id);
      setMorning(!!row.morning);
      setAfternoon(!!row.afternoon);
      setEvening(!!row.evening);
      setNight(!!row.night);
      setDuration(row.duration ?? '');
      setDurationMode(row.duration_mode ?? 1);
      setInstructions(row.instructions ?? '');
      setComments(row.comments ?? '');
      setDrugRows(
        (row.drugs && row.drugs.length) > 0
          ? row.drugs.map((d) => ({
            id: d.id,
            drug_ms_id: d.drug_ms_id,
            drug_name: d.drug_name,
            quantity: d.quantity ?? '',
            uom: d.uom ?? 'g',
            medicineList: d.drug_name ? [{ drug_name: d.drug_name, drug_ms_id: d.drug_ms_id, generic_name: d.generic_name || '' }] : [],
          }))
          : [emptyDrugRow()]
      );
    };

    const handlePrint = () => {
      try {
        if (!ip_admission_id) return;
        setIsLoader(true);
        RestAPIService.getAll(`${Serviceurls.IP_MEDICATION_DETAILS}?ip_admission_id=${ip_admission_id}&export_type=pdf`)
          .then((response) => {
            const file = new Blob([response.data], { type: "application/pdf" });
            window.open(URL.createObjectURL(file));
            setIsLoader(false);
          })
          .catch((e) => {
            showError(e?.response?.data?.message || e?.message || "Print failed");
            setIsLoader(false);
          })
          .finally(() => setIsLoader(false));
      } catch (e) {
        showError(e?.response?.data?.message || e?.message || "Print failed");
        setIsLoader(false);
      }
    };


    const formatMaen = (row) => {
      const m = row.morning ? 1 : 0;
      const a = row.afternoon ? 1 : 0;
      const e = row.evening ? 1 : 0;
      const n = row.night ? 1 : 0;
      const dur = row.duration ? `${row.duration} ${DURATION_MODE_MAP[row.duration_mode] || 'Day(s)'}` : '';
      return `${m}-${a}-${e}-${n}${dur ? `/${dur}` : ''}`;
    };

    const paginatedList = filteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Box sx={{ backgroundColor: Colors.configHomeBg, minHeight: '74dvh', p: '0.625vw' }}>
        <Box component={'div'} className='doc_header_card' mr={'0.5vw'} mb={.5}>
          <CommonPatientDetails data={patientData} showDetailed={true} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', height: '62dvh' }}>
          {/* Left: Medication list */}
          <Paper variant="outlined" sx={{ flex: '1 1 65%', minWidth: 320, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, p: .5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <ButtonGroup size="small" disableElevation>
                {STATUS_TABS.map((t) => (
                  <Button
                    key={t.key}
                    variant={statusTab === t.key ? 'contained' : 'outlined'}
                    onClick={() => setStatusTab(t.key)}
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {t.label}
                  </Button>
                ))}
              </ButtonGroup>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CompareArrowsIcon />}
                  onClick={handleTransferDrugs}
                  disabled={selectedPrescriptionIds.length === 0 && UserData?.module_name !== 'Doctor'}
                  sx={{ textTransform: 'uppercase', bgcolor: Colors.Primary || '#1976d2' }}
                >
                  Transfer Drugs ({selectedPrescriptionIds.length})
                </Button>
                <IconButton size="small" onClick={handlePrint} title="Print">
                  <PrintIcon />
                </IconButton>
              </Box>
            </Box>
            <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { borderRight: '1px solid', borderColor: 'divider' } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell padding="checkbox" sx={{ borderRight: '1px solid', borderColor: 'divider' }} />
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Brand Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>UOM</TableCell>
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>M-A-E-N / Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Usage</TableCell>
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Status</TableCell>
                  {UserData?.module_name === 'Doctor' ? <TableCell align="center" sx={{ fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>Actions</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedList.map((row, rowIndex) => {
                  const drugs = row.drugs || [];
                  const drugCount = drugs.length || 1;
                  const canSelect = statusTab === 'request' && (row.prescription_status || row.status) === 'Pending';
                  const checked = selectedPrescriptionIds.includes(row.prescription_id);
                  const borderColor = rowIndex % 2 === 0 ? 'gray' : '#4da3d4';
                  const cellSx = { verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' };
                  return (
                    <React.Fragment key={row.prescription_id ?? row.id}>
                      {drugs.map((drug, drugIndex) => {
                        const isFirst = drugIndex === 0;
                        return (
                          <TableRow key={`${row.prescription_id}-${drug?.id ?? drugIndex}`} hover sx={{ borderLeft: `5px solid ${borderColor}` }}>
                            {isFirst && (
                              <TableCell padding="checkbox" rowSpan={drugCount} sx={cellSx}>
                                {canSelect && (
                                  <Checkbox
                                    checked={checked}
                                    onChange={(e) => togglePrescriptionSelection(row.prescription_id, e.target.checked)}
                                  />
                                )}
                              </TableCell>
                            )}
                            {isFirst && (
                              <TableCell rowSpan={drugCount} sx={cellSx}>{row.prescription_date || '-'}</TableCell>
                            )}
                            <TableCell sx={cellSx}>{drug.drug_name || drug.generic_name || '-'}</TableCell>
                            <TableCell sx={cellSx}>{drug.quantity != null ? drug.quantity : '-'}</TableCell>
                            <TableCell sx={cellSx}>{drug.uom ?? '-'}</TableCell>
                            {isFirst && (
                              <TableCell rowSpan={drugCount} sx={cellSx}>{formatMaen(row)}</TableCell>
                            )}
                            {isFirst && (
                              <TableCell rowSpan={drugCount} sx={cellSx}>{row.instructions || '-'}</TableCell>
                            )}
                            {isFirst && (
                              <TableCell rowSpan={drugCount} sx={cellSx}>{row.prescription_status || row.status || 'Pending'}</TableCell>
                            )}
                            {isFirst && UserData?.module_name === 'Doctor' ? (
                              <TableCell align="center" rowSpan={drugCount} sx={cellSx}>
                                <IconButton size="small" disabled={row.prescription_status !== 'Pending'} onClick={() => handleEdit(row)} title="Edit">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" disabled={row.prescription_status !== 'Pending'} onClick={() => handleDelete(row.prescription_id)} title="Delete">
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            ) : null}
                          </TableRow>
                        );
                      })}
                      {drugs.length === 0 && (
                        <TableRow sx={{ borderLeft: `5px solid ${borderColor}` }}>
                          <TableCell padding="checkbox" sx={cellSx}>
                            {canSelect && (
                              <Checkbox
                                checked={checked}
                                onChange={(e) => togglePrescriptionSelection(row.prescription_id, e.target.checked)}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={cellSx}>{row.prescription_date || '-'}</TableCell>
                          <TableCell sx={cellSx}>-</TableCell>
                          <TableCell sx={cellSx}>-</TableCell>
                          <TableCell sx={cellSx}>-</TableCell>
                          <TableCell sx={cellSx}>{formatMaen(row)}</TableCell>
                          <TableCell sx={cellSx}>{row.instructions || '-'}</TableCell>
                          <TableCell sx={cellSx}>{row.prescription_status || row.status || 'Pending'}</TableCell>
                          {UserData?.module_name === 'Doctor' ? <TableCell align="center" sx={cellSx}>
                            <IconButton size="small" disabled={row.prescription_status !== 'Pending'} onClick={() => handleEdit(row)} title="Edit">
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" disabled={row.prescription_status !== 'Pending'} onClick={() => handleDelete(row.prescription_id)} title="Delete">
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell> : null}
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredList.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Rows per page:"
            />
            {filteredList.length === 0 && !isLoader && (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No records</Typography>
            )}
          </Paper>

          {/* Right: Details For Drug */}
          <Paper variant="outlined" sx={{ flex: '0 1 30%', p: 1, height: 'fit-content' }}>
            <Box sx={{ height: '55dvh', overflow: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Add Medication
                </Typography>
                <FormControl size="small" sx={{ mb: 1, minWidth: 150 }}>
                  <InputLabel>Select Pharmacy</InputLabel>
                  <Select
                    value={pharmacyId ?? ''}
                    label="Select Pharmacy"
                    onChange={(e) => setPharmacyId(e.target.value)}
                  >
                    {pharmacyList.map((ph) => (
                      <MenuItem key={ph.pharmacy_id ?? ph.id} value={ph.pharmacy_id ?? ph.id}>
                        {ph.pharmacy_name ?? ph.name ?? ph.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {drugRows.map((row, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                  <Autocomplete
                    size="small"
                    options={row.medicineList || []}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : `${o?.drug_name || ''} - ${o?.generic_name || ''}`.trim() || '')}
                    value={
                      (row.medicineList || []).find((m) => m.drug_name === row.drug_name) ||
                      (row.drug_name ? { drug_name: row.drug_name, drug_ms_id: row.drug_ms_id, generic_name: '' } : null)
                    }
                    onInputChange={(e, value) => drugSearchApi(value, index)}
                    onChange={(e, v) => setDrugRowFromOption(index, v)}
                    renderInput={(params) => <TextField {...params} placeholder="Drug name" />}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Qty"
                    value={row.quantity}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    onChange={(e) => setDrugRow(index, 'quantity', e.target.value)}
                    sx={{ width: 90 }}
                  />
                  <TextField
                    size="small"
                    placeholder="UOM"
                    value={row.uom}
                    onChange={(e) => setDrugRow(index, 'uom', e.target.value)}
                    sx={{ width: 70 }}
                  />
                  {index === 0 ? (
                    <IconButton size="small" color="primary" onClick={addDrugRow}>
                      <AddIcon />
                    </IconButton>
                  ) : (
                    <IconButton size="small" color="error" onClick={() => removeDrugRow(index)}>
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>
              ))}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {['Morning', 'Afternoon', 'Evening', 'Night'].map((label, i) => {
                  const state = [morning, afternoon, evening, night][i];
                  const setState = [setMorning, setAfternoon, setEvening, setNight][i];
                  return (
                    <Button
                      key={label}
                      size="small"
                      variant={state ? 'contained' : 'outlined'}
                      onClick={() => setState(!state)}
                      sx={{ textTransform: 'capitalize', minWidth: 80 }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Duration *</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {DURATION_DAYS.map((d) => (
                  <Button
                    key={d}
                    size="small"
                    variant={String(duration) === String(d) ? 'contained' : 'outlined'}
                    onClick={() => setDuration(d)}
                    sx={{ minWidth: 32 }}
                  >
                    {d}
                  </Button>
                ))}
              </Box>
              <FormControl size="small" sx={{ minWidth: 100, mb: 2 }}>
                <Select value={durationMode} onChange={(e) => setDurationMode(Number(e.target.value))}>
                  <MenuItem value={1}>Day(s)</MenuItem>
                  <MenuItem value={2}>Week(s)</MenuItem>
                  <MenuItem value={3}>Month(s)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Select Usage *</InputLabel>
                <Select
                  value={instructions ?? ''}
                  label="Select Usage *"
                  onChange={(e) => setInstructions(e.target.value)}
                >
                  {instructionList.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                inputProps={{ maxLength: 250 }}
                label="Comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                sx={{ mb: 2 }}
              />
            </Box>
            {UserData?.module_name === 'Doctor' ?
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
                <Button variant="outlined" size="small" onClick={handleClearForm} sx={{ textTransform: 'capitalize' }}>
                  Clear
                </Button>
                <Button variant="contained" size="small" onClick={handleAddOrUpdate} sx={{ textTransform: 'capitalize', bgcolor: Colors.Primary }}>
                  {editingPrescriptionId ? 'Update' : 'Add'}
                </Button>
              </Box>
              : null
            }
          </Paper>
        </Box>

        {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
        {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
        <Loader loaderOpen={isLoader} />
      </Box>
    );
  }
